import React, { useState, useEffect } from 'react'
import fetcher from '@lib/fetcher'
import fetch from '@lib/fetch'

function UpcomingEventsStrip() {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState({})

  useEffect(() => {
    loadEvents()
  }, [])

  const loadEvents = async () => {
    try {
      const data = await fetcher('/events/upcoming')
      setEvents(data.events || [])
    } catch (error) {
      console.error('Failed to load events:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleHero = async (event, e) => {
    e.stopPropagation()
    e.preventDefault()
    
    setUpdating(prev => ({ ...prev, [event.id]: true }))
    
    try {
      const response = await fetch(`/events/${event.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ event: { hero: !event.hero } })
      })
      
      if (response.ok) {
        const updatedEvent = await response.json()
        setEvents(prev => prev.map(e => 
          e.id === event.id ? { ...e, hero: updatedEvent.hero } : e
        ))
      }
    } catch (error) {
      console.error('Failed to toggle hero status:', error)
    } finally {
      setUpdating(prev => ({ ...prev, [event.id]: false }))
    }
  }

  const handleEventClick = (event, e) => {
    // Don't navigate if clicking on the toggle
    if (e.target.closest('.hero-toggle')) return
    
    window.location.href = `/events/${event.slug}/edit`
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Upcoming Hero Events</h2>
        <div className="flex space-x-4 overflow-x-auto pb-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex-shrink-0 animate-pulse">
              <div className="w-40 h-30 bg-gray-200 dark:bg-gray-700 rounded-lg mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Upcoming Hero Events</h2>
      
      {events.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No upcoming events</p>
      ) : (
        <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          {events.map(event => (
            <div 
              key={event.id} 
              className="flex-shrink-0 w-40 cursor-pointer group"
              onClick={(e) => handleEventClick(event, e)}
            >
              <div className="relative">
                {/* Event Image */}
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 mb-2">
                  {event.image_url ? (
                    <img 
                      src={event.image_url} 
                      alt={event.name}
                      className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Hero Badge - positioned on left side */}
                {event.hero && (
                  <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    hero
                  </div>
                )}

                {/* Hero Toggle - positioned in top right corner */}
                <div className="hero-toggle absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={event.hero || false}
                      onChange={(e) => toggleHero(event, e)}
                      disabled={updating[event.id]}
                    />
                    <div className={`
                      w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer 
                      dark:bg-gray-600 peer-checked:after:translate-x-full 
                      peer-checked:after:border-white after:content-[''] after:absolute 
                      after:top-[2px] after:left-[2px] after:bg-white after:rounded-full 
                      after:h-4 after:w-4 after:transition-all dark:border-gray-600 
                      peer-checked:bg-green-600
                      ${updating[event.id] ? 'opacity-50 cursor-not-allowed' : ''}
                    `}></div>
                  </label>
                </div>
              </div>

              {/* Event Name */}
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {event.name}
              </p>
              
              {/* Event Date */}
              {event.first_instance_date && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(event.first_instance_date).toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short' 
                  })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default UpcomingEventsStrip